import React from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Jednotná sekcia rodiny detektorov (GDPR / NIS2 / VYHRADENÉ).
// Presentational + generická: rodič dodá isChecked/onToggle/onSetMany, takže
// rovnaký vzhľad funguje pre boolean flagy (GDPR) aj pole kľúčov (NIS2/VYHRADENÉ).
//
// props:
//   title, subtitle, Icon
//   groups: [{ group, label, detectors: [{ key, label, info }] }]
//   isChecked(key) -> bool
//   onToggle(key) -> void
//   onSetMany(keys[], value) -> void
//   footer?: ReactNode (napr. pod-voľby mien pre GDPR)
const DetectorInfo = ({ info }) => {
  if (!info) return null;
  return (
    <Tooltip
      title={info}
      arrow
      placement="top"
      enterTouchDelay={0}
      leaveTouchDelay={8000}
      componentsProps={{
        tooltip: { sx: { maxWidth: 360, fontSize: 12, lineHeight: 1.55, p: 1.25, bgcolor: "grey.900" } },
        arrow: { sx: { color: "grey.900" } },
      }}
    >
      <IconButton
        size="small"
        onClick={(e) => e.stopPropagation()}
        sx={{ p: 0.25, ml: 0.25, color: "text.disabled", "&:hover": { color: "primary.main" } }}
        aria-label="Vysvetlenie detektora"
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};

const DetectorFamilySection = ({
  title,
  subtitle,
  groups = [],
  isChecked = () => false,
  onToggle = () => {},
  onSetMany = () => {},
  Icon,
  footer = null,
}) => {
  const allKeys = groups.flatMap((g) => g.detectors.map((d) => d.key));
  const selectedCount = allKeys.filter((k) => isChecked(k)).length;
  const allOn = allKeys.length > 0 && selectedCount === allKeys.length;

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {Icon && <Icon fontSize="small" color="primary" />}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={`${selectedCount}/${allKeys.length}`}
            color={selectedCount ? "primary" : "default"}
            variant={selectedCount ? "filled" : "outlined"}
          />
        </Box>
        <Button size="small" onClick={() => onSetMany(allKeys, !allOn)} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {allOn ? "Vypnúť všetko" : "Zapnúť všetko"}
        </Button>
      </Box>

      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
          {subtitle}
        </Typography>
      )}

      {groups.map((g, gi) => {
        const groupKeys = g.detectors.map((d) => d.key);
        const groupAllOn = groupKeys.every((k) => isChecked(k));
        const showGroupHeader = groups.length > 1;
        return (
          <Box key={g.group} sx={{ mt: gi === 0 ? 1.5 : 2 }}>
            {showGroupHeader && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {g.label}
                </Typography>
                <Button
                  size="small"
                  onClick={() => onSetMany(groupKeys, !groupAllOn)}
                  sx={{ minWidth: 0, px: 1, fontSize: 11, color: "text.secondary" }}
                >
                  {groupAllOn ? "− skupina" : "+ skupina"}
                </Button>
              </Box>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 0.25,
                mt: 0.5,
              }}
            >
              {g.detectors.map((d) => {
                const active = isChecked(d.key);
                return (
                  <Box
                    key={d.key}
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      pr: 0.5,
                      borderRadius: 1.5,
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.06) : "transparent",
                    })}
                  >
                    <FormControlLabel
                      onClick={(e) => e.stopPropagation()}
                      control={
                        <Checkbox size="small" checked={active} onChange={() => onToggle(d.key)} sx={{ py: 0.25 }} />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: active ? "text.primary" : "text.secondary" }}>
                          {d.label}
                        </Typography>
                      }
                      sx={{ m: 0, px: 1, flex: 1, minWidth: 0 }}
                    />
                    <DetectorInfo info={d.info} />
                  </Box>
                );
              })}
            </Box>
            {gi < groups.length - 1 && <Divider sx={{ mt: 1.5 }} />}
          </Box>
        );
      })}

      {footer}
    </Box>
  );
};

export default DetectorFamilySection;
